// Allowing prop sprading as the Idea component is just a wrapper
// and needs to pass props to different individual components
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Idea as IdeaType } from '~/content/types';
import TrashIcon from '@bufferapp/ui/Icon/Icons/Trash';
import Button from '@bufferapp/ui/Button';
import Tooltip from '@bufferapp/ui/Tooltip';

import { CARD_PADDING, GRID_GAP } from '../config';

import {
  IdeaStyled,
  IdeaStyledMedia,
  TwoColumnsMediaGrid,
  TwoRowsMediaGrid,
  TwoColumnsTwoRowsMediaGrid,
  DeleteIdeaContainer,
  SimpleMediaGrid,
} from './styles';

import { isTextIdea, isImageIdea, isVideoIdea, isMixedIdea } from './helpers';
import { useIdeaText } from '../hooks/useIdeaText';
import { useIdeaMedia } from '../hooks/useIdeaMedia';

const DeleteIdea = ({
  idea,
  onDelete,
}: Omit<IdeaProps, 'onClick'>): JSX.Element => {
  const onClick = (e) => {
    e.stopPropagation();
    onDelete(idea);
  };

  return (
    <DeleteIdeaContainer>
      <Tooltip label="Delete">
        <Button
          type="danger"
          onClick={onClick}
          label="Delete Idea"
          icon={<TrashIcon />}
          hasIconOnly
        />
      </Tooltip>
    </DeleteIdeaContainer>
  );
};

const IdeaText = ({ idea, onClick, onDelete }: IdeaProps): JSX.Element => {
  const { text } = useIdeaText({ idea });

  const handleClick = () => onClick(idea);

  if (idea && idea.content.text) {
    return (
      <IdeaStyled key={idea.id} onClick={handleClick}>
        <DeleteIdea idea={idea} onDelete={onDelete} />
        {text}
      </IdeaStyled>
    );
  }
  return <></>;
};

type IdeaMediaProps = {
  idea: IdeaType;
  isChild?: boolean;
};

const IdeaMediaSingle = ({
  idea,
  isChild = false,
}: IdeaMediaProps): JSX.Element => {
  const { media } = useIdeaMedia({ idea });

  if (idea && idea.content.media) {
    const height = (isChild ? 7 : 13) * GRID_GAP;
    return (
      <>
        <SimpleMediaGrid style={{ height }}>{media}</SimpleMediaGrid>
      </>
    );
  }
  return <></>;
};

const IdeaMediaDouble = ({
  idea,
  isChild = false,
}: IdeaMediaProps): JSX.Element => {
  const { media: firstMedia } = useIdeaMedia({
    idea,
    mediaIndex: 0,
  });
  const { media: secondMedia } = useIdeaMedia({
    idea,
    mediaIndex: 1,
  });

  if (idea && idea.content.media) {
    const height = (isChild ? 7 : 10) * GRID_GAP;
    return (
      <>
        <TwoRowsMediaGrid style={{ height }}>
          {firstMedia}
          {secondMedia}
        </TwoRowsMediaGrid>
      </>
    );
  }
  return <></>;
};

const IdeaMediaTriple = ({
  idea,
  isChild = false,
}: IdeaMediaProps): JSX.Element => {
  const { media: firstMedia } = useIdeaMedia({
    idea,
    mediaIndex: 0,
  });
  const { media: secondMedia } = useIdeaMedia({
    idea,
    mediaIndex: 1,
  });
  const { media: thirdMedia } = useIdeaMedia({
    idea,
    mediaIndex: 2,
  });

  if (idea && idea.content.media) {
    const height = (isChild ? 7 : 13) * GRID_GAP;
    return (
      <>
        <TwoColumnsMediaGrid style={{ height }}>
          {firstMedia}
          <TwoRowsMediaGrid>
            {secondMedia}
            {thirdMedia}
          </TwoRowsMediaGrid>
        </TwoColumnsMediaGrid>
      </>
    );
  }
  return <></>;
};

const IdeaMediaQuadruple = ({
  idea,
  isChild = false,
}: IdeaMediaProps): JSX.Element => {
  const { media: firstMedia } = useIdeaMedia({
    idea,
    mediaIndex: 0,
  });
  const { media: secondMedia } = useIdeaMedia({
    idea,
    mediaIndex: 1,
  });
  const { media: thirdMedia } = useIdeaMedia({
    idea,
    mediaIndex: 2,
  });
  const { media: fourthMedia } = useIdeaMedia({
    idea,
    mediaIndex: 3,
  });

  if (idea && idea.content.media) {
    const height = (isChild ? 7 : 10) * GRID_GAP;
    return (
      <>
        <TwoColumnsTwoRowsMediaGrid style={{ height }}>
          {firstMedia}
          {secondMedia}
          {thirdMedia}
          {fourthMedia}
        </TwoColumnsTwoRowsMediaGrid>
      </>
    );
  }
  return <></>;
};

const IdeaMediaContent = ({
  idea,
  isChild = false,
}: IdeaMediaProps): JSX.Element => {
  if (idea.content.media?.length) {
    if (idea && idea.content.media?.length === 1) {
      return <IdeaMediaSingle idea={idea} isChild={isChild} />;
    }

    if (idea && idea.content.media?.length === 2) {
      return <IdeaMediaDouble idea={idea} isChild={isChild} />;
    }

    if (idea && idea.content.media?.length === 3) {
      return <IdeaMediaTriple idea={idea} isChild={isChild} />;
    }

    if (idea && idea.content.media?.length >= 4) {
      return <IdeaMediaQuadruple idea={idea} isChild={isChild} />;
    }
  }

  return <></>;
};

const IdeaMedia = ({ idea, onClick, onDelete }: IdeaProps): JSX.Element => {
  const handleClick = () => onClick(idea);
  return (
    <IdeaStyledMedia key={idea.id} onClick={handleClick}>
      <DeleteIdea idea={idea} onDelete={onDelete} />
      <IdeaMediaContent idea={idea} />
    </IdeaStyledMedia>
  );
};

const IdeaMixed = ({ idea, onClick, onDelete }: IdeaProps): JSX.Element => {
  const { text } = useIdeaText({
    idea,
    lines: 4,
  });

  const handleClick = () => onClick(idea);

  if (idea && idea.content.media) {
    return (
      <IdeaStyledMedia key={idea.id} onClick={handleClick}>
        <DeleteIdea idea={idea} onDelete={onDelete} />
        <IdeaMediaContent idea={idea} isChild />
        <div
          style={{
            padding: `${CARD_PADDING}px`,
          }}
        >
          {text}
        </div>
      </IdeaStyledMedia>
    );
  }
  return <></>;
};

type IdeaProps = {
  idea: IdeaType;
  onClick: (idea: IdeaType) => void;
  onDelete: (idea: IdeaType) => void;
};

export const Idea = (ideaProps: IdeaProps) => {
  const { idea } = ideaProps;
  if (isTextIdea(idea)) return <IdeaText {...ideaProps} />;
  if (isImageIdea(idea)) return <IdeaMedia {...ideaProps} />;
  if (isVideoIdea(idea)) return <IdeaMedia {...ideaProps} />;
  if (isMixedIdea(idea)) return <IdeaMixed {...ideaProps} />;
  return <></>;
};
